<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  xs?: boolean
  sm?: boolean
  md?: boolean
  lg?: boolean
  xl?: boolean
  full?: boolean
  primaryColor?: string
  secondaryColor?: string
  accentColor?: string
}>()

// Default colors (original brand colors)
const primaryColor = computed(() => props.primaryColor || '#5F0CE2')
const secondaryColor = computed(() => props.secondaryColor || '#644AFF')
const accentColor = computed(() => props.accentColor || '#04F5B9')

// Generate unique IDs for gradients to avoid conflicts
const gradientId = computed(() => `paint_gradient_${Math.random().toString(36).substr(2, 9)}`)
const paperclipGradientId = computed(() => `paperclip_gradient_${Math.random().toString(36).substr(2, 9)}`)

const sizeClass = computed(() => {
  if (props.full) return 'w-full'
  if (props.xl) return 'w-96'
  if (props.lg) return 'w-80'
  if (props.md) return 'w-64'
  if (props.sm) return 'w-48'
  if (props.xs) return 'w-32'
  return 'w-64'
})
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="91.63 95.27 731.94 83"
  >
    <path
      d="M306.531 119.136C314.699 111.136 324.169 108.687 334.905 111.972C343.179 114.5 348.083 121.119 348.228 129.789C348.392 139.392 348.266 148.995 348.266 158.841H331.912C331.854 157.752 331.748 156.596 331.738 155.439C331.719 148.237 331.767 141.025 331.709 133.822C331.68 129.225 330.705 124.919 325.83 123.131C320.646 121.226 315.587 121.993 311.522 125.794C310.229 127.009 309.524 129.419 309.476 131.315C309.254 139.275 309.379 147.255 309.379 155.225C309.379 156.401 309.379 157.568 309.379 158.909H292.677C292.677 157.704 292.677 156.547 292.677 155.39C292.668 147.634 292.764 139.887 292.6 132.131C292.465 125.648 288.593 122.168 282.115 122.314C274.083 122.499 269.41 127.232 269.41 135.203C269.41 143.037 269.41 150.871 269.41 158.899H253.008V111.836H269.169C269.314 113.401 269.459 114.966 269.613 116.589C286.634 107.851 296.24 108.493 306.531 119.136Z"
      fill="#141414"
    />
    <path
      d="M370.7 153.799V176.727H354.666V112.461H370.539C370.672 113.882 370.794 115.198 370.917 116.609C385.045 107.716 399.504 108.682 408.549 118.996C416.283 127.832 416.302 141.592 408.813 150.712C401.258 159.908 383.093 163.137 370.709 153.799H370.7ZM384.593 147.757C393.025 147.786 397.901 143.126 397.948 134.991C397.995 127.093 393.025 122.121 385.008 122.045C376.123 121.96 370.813 126.695 370.766 134.726C370.719 143.032 375.699 147.729 384.593 147.757Z"
      fill="#141414"
    />
    <path
      d="M246.325 136.866H204.926C205.712 142.78 209.794 146.839 215.58 147.758C222.728 148.891 227.922 146.027 231.518 138.891H246.422C245.627 143.967 243.277 147.999 239.725 151.344C229.203 161.263 207.894 161.451 197.134 151.772C185.393 141.21 187.389 122.548 201.436 114.948C212.215 109.114 223.514 109.141 234.469 114.654C243.118 119.007 247.986 128.436 246.334 136.857L246.325 136.866ZM229.99 127.856C226.12 122.317 221.076 121.228 215.474 121.995C211.808 122.495 208.716 124.19 206.605 127.856H229.99Z"
      fill="#141414"
    />
    <path
      d="M620.107 136.822H577.795C577.876 141.971 581.66 146.085 587.386 147.477C594.891 149.306 600.671 146.477 604.844 138.91H620.044C619.123 144.211 616.576 148.405 612.656 151.805C601.963 161.077 581.236 161.3 570.262 152.314C557.573 141.917 559.379 122.659 574.092 114.896C585.02 109.131 596.481 109.184 607.599 114.53C616.404 118.76 621.137 127.496 620.107 136.813V136.822ZM602.992 127.8C598.125 119.179 582.374 120.518 579.673 127.8H602.992Z"
      fill="#141414"
    />
    <path
      d="M472.198 158.907C466.291 158.961 460.649 157.923 455.51 154.936C447.925 150.535 443.669 143.881 443.616 135.044C443.563 126.207 447.616 119.222 455.298 115.063C466.282 109.115 477.734 109.044 488.798 114.83C496.639 118.927 500.789 125.859 500.798 134.892C500.798 143.926 496.489 150.562 488.833 155.016C483.694 158.004 478.035 158.979 472.189 158.898L472.198 158.907ZM472.304 121.807C464.242 121.807 458.671 127.191 458.697 134.955C458.732 142.566 464.26 147.915 472.154 147.968C480.207 148.031 485.893 142.647 485.911 134.955C485.937 127.209 480.348 121.816 472.295 121.807H472.304Z"
      fill="#141414"
    />
    <path
      d="M512.379 176.668C515.56 171.243 518.299 166.325 521.297 161.561C522.556 159.559 522.527 157.984 521.537 155.865C515.012 141.954 508.631 127.975 502.221 114.015C501.779 113.053 501.395 112.062 500.799 110.633C506.353 110.633 511.514 110.535 516.665 110.739C517.453 110.769 518.472 112.071 518.885 113.004C522.527 121.16 526.054 129.375 529.629 137.56C530.052 138.532 530.551 139.465 531.234 140.875C535.395 131.416 539.393 122.483 543.227 113.481C544.111 111.41 545.082 110.438 547.484 110.555C552.107 110.778 556.739 110.623 561.794 110.623C561.14 112.091 560.708 113.17 560.189 114.21C550.396 134.051 540.575 153.862 530.859 173.732C529.812 175.861 528.678 176.843 526.208 176.716C521.874 176.503 517.53 176.658 512.389 176.658L512.379 176.668Z"
      fill="#141414"
    />
    <path
      d="M696.401 125.321C694.704 121.225 689.576 120.441 687.012 121.74C685.811 122.376 684.402 123.596 684.457 125.435C684.511 127.3 685.991 127.945 687.174 128.337C690.921 129.566 694.848 130.263 698.64 131.387C701.15 132.128 703.687 133.017 705.971 134.246C714.647 138.891 713.049 150.49 704.427 155.257C697.205 159.388 686.47 159.606 680.692 157.924C674.914 156.242 672.431 154.186 669.614 150.456C667.899 148.181 667.158 145.27 667.276 140.329C671.952 140.329 675.239 140.329 679.825 140.329C679.997 142.22 680.421 143.632 681.08 144.808C683.599 149.253 690.912 150.29 694.893 147.484C696.022 146.691 696.961 145.305 696.997 143.423C697.069 141.662 695.02 140.642 693.711 140.303C689.097 139.1 684.375 138.298 679.789 137.043C677.676 136.468 675.618 135.422 673.785 134.228C666.292 129.339 668.504 119.125 674.643 115.055C681.251 110.062 693.07 108.85 701.665 113.277C707.199 116.694 710.323 120.894 710.431 128.197H696.961C696.961 128.197 696.898 126.585 696.392 125.321H696.401Z"
      fill="#141414"
    />
    <path
      d="M794.067 158.906C788.962 158.936 783.419 157.902 778.372 154.979C770.67 150.549 766.352 143.816 766.381 134.839C766.381 126.009 770.524 119.069 778.226 114.965C789.137 109.147 800.515 109.058 811.484 114.787C819.449 118.922 823.65 125.92 823.562 135.075C823.504 144.111 819.128 150.697 811.426 155.097C806.262 158.05 800.602 158.965 794.038 158.906H794.067ZM795.118 121.816C787.036 121.816 781.435 127.161 781.464 134.927C781.464 142.546 787.007 147.921 794.884 147.98C802.936 148.039 808.654 142.694 808.683 135.016C808.713 127.279 803.14 121.845 795.118 121.816Z"
      fill="black"
    />
    <path
      d="M743.508 114.364H758.757V158.909H743.508V114.364Z"
      fill="black"
    />
    <path
      d="M743.508 95.2727H758.757V108H743.508V95.2727Z"
      fill="black"
    />
    <path
      d="M719.363 146.182H734.612V158.909H719.363V146.182Z"
      fill="black"
    />
    <path
      d="M436.716 95.2727H420.611V159.063H436.716V95.2727Z"
      fill="#141414"
    />
    <path
      d="M644.1 113.01C644.205 114.502 644.3 115.87 644.434 117.812C649.481 113.622 654.919 111.594 661.456 112.407V125.188C658.589 125.532 655.789 125.638 653.103 126.25C646.652 127.714 644.272 130.813 644.272 137.299C644.272 144.474 644.272 151.64 644.272 158.977H628.033V113.01H644.109H644.1Z"
      fill="#141414"
    />
    <!-- Paperclip - uses country flag gradient -->
    <path
      d="M168.751 136.331L129.393 159.196C118.054 165.784 103.172 163.003 95.844 152.114C87.8126 140.173 91.659 124.069 103.893 116.96L137.503 97.4321C144.944 93.1128 154.477 95.6418 158.792 103.09C163.108 110.538 160.581 120.08 153.14 124.399L119.521 143.936C116.413 145.743 112.428 144.683 110.622 141.572C108.816 138.461 109.875 134.472 112.983 132.664L146.602 113.127C147.826 112.415 148.243 110.85 147.531 109.625C146.819 108.4 145.256 107.982 144.032 108.695L110.865 127.962C105.612 131.013 103.137 137.574 105.664 143.101C108.555 149.446 116.222 151.775 122.1 148.359L162.205 125.051C165.313 123.243 169.298 124.304 171.104 127.415C172.91 130.526 171.851 134.515 168.742 136.323L168.751 136.331Z"
      :fill="`url(#${paperclipGradientId})`"
    />
    <!-- Gradient overlay -->
    <path
      d="M130.408 158.606L168.75 136.331L162.264 125.025L122.107 148.368L130.408 158.606Z"
      :fill="`url(#${gradientId})`"
    />
    <!-- Dot accent - uses country accent color -->
    <path
      d="M165.488 137.2C169.079 137.2 171.991 134.286 171.991 130.691C171.991 127.096 169.079 124.182 165.488 124.182C161.896 124.182 158.984 127.096 158.984 130.691C158.984 134.286 161.896 137.2 165.488 137.2Z"
      :fill="accentColor"
    />
    <defs>
      <!-- Paperclip gradient with flag colors -->
      <linearGradient
        :id="paperclipGradientId"
        x1="95"
        y1="130"
        x2="172"
        y2="130"
        gradientUnits="userSpaceOnUse"
      >
        <stop
          offset="0%"
          :stop-color="primaryColor"
        />
        <stop
          offset="50%"
          :stop-color="secondaryColor"
        />
        <stop
          offset="100%"
          :stop-color="primaryColor"
        />
      </linearGradient>
      <!-- Overlay gradient -->
      <linearGradient
        :id="gradientId"
        x1="125.367"
        y1="154.024"
        x2="165.659"
        y2="130.38"
        gradientUnits="userSpaceOnUse"
      >
        <stop
          :stop-color="secondaryColor"
          stop-opacity="0"
        />
        <stop
          offset="1"
          :stop-color="accentColor"
        />
      </linearGradient>
    </defs>
  </svg>
</template>
